<template>
  <div>
    <a-row :gutter="0">
      <a-col :offset="18" :span="6">
        <a-input
          placeholder="Search"
          class="search-input"
          v-model="searchKeyword"
        />
      </a-col>
    </a-row>
    <a-row :gutter="0">
      <a-col :span="24">
        <a-table
          tableLayout="auto"
          bordered
          :columns="columns"
          :components="components"
          :data-source="dataSource"
          :loading="loading"
          :pagination="pagination"
          @change="handleTableChange"
          :rowKey="defaultRowKey"
          size="middle"
          :row-selection="
            showRowSeletion
              ? { selectedRowKeys: selectedRowKeys, onChange: onSelectChange }
              : null
          "
        >
          <template
            v-for="(_, slotName) in $scopedSlots"
            v-slot:[slotName]="props"
          >
            <slot :name="slotName" v-bind="props" />
          </template>
        </a-table>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import Vue from "vue";
import VueDraggableResizable from "vue-draggable-resizable";

Vue.component("vue-draggable-resizable", VueDraggableResizable);

export default {
  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    dataSource: {
      type: Array,
      default: () => [],
    },
    rowKey: {
      type: String,
      default: "id",
    },
    defaultSortField: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showRowSeletion: {
      type: Boolean,
      default: false,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    this.components = {
      header: {
        cell: this.resizeableTitle,
      },
    };
    return {
      pagination: {},
      sorter: { field: this.defaultSortField, order: "ascend" },
      draggingMap: [],
      searchKeyword: "",
      searchKeywordTimeout: null,
      selectedRowKeys: [],
    };
  },
  computed: {
    defaultRowKey() {
      return this.rowKey ? this.rowKey : "id";
    },
  },
  watch: {
    searchKeyword(val) {
      if (!val || val.length >= 3) {
        const self = this;
        this.pagination.current = 1;
        this.searchKeywordTimeout && clearTimeout(this.searchKeywordTimeout);
        this.searchKeywordTimeout = setTimeout(
          () => self.refreshData({ searchKeyword: val ?? "" }),
          500
        );
      }
    },
    totalCount() {
      this.pagination.total = this.totalCount;
    },
  },
  mounted() {
    this.pagination = {
      pageSize: 12,
      current: 1,
      total: this.totalCount,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    };
    this.columns.forEach((col) => {
      this.draggingMap[col.key] = col.width;
    });
  },
  methods: {
    handleTableChange(pagination, filters, sorter) {
      this.pagination = {
        ...this.pagination,
        current: pagination.current,
        total: this.totalCount,
      };
      this.sorter = { ...sorter };

      this.refreshData();
    },
    refreshData(isDeleteRefresh = false) {
      if (isDeleteRefresh) {
        if (
          (this.pagination.current - 1) * this.pagination.pageSize >=
          this.totalCount
        ) {
          this.pagination.current =
            this.pagination.current > 1 ? this.pagination.current - 1 : 1;
        }
      }

      this.$emit("refreshData", {
        pageSize: this.pagination.pageSize,
        pageIndex: this.pagination.current,
        sortField: this.sorter.field,
        sortOrder: this.sorter.order,
        searchKeyword: this.searchKeyword,
      });
    },
    resizeableTitle(h, props, children) {
      const self = this;
      let thDom = null;

      const { key, ...restProps } = props;
      const col = this.columns.find(
        (col) => (col.dataIndex || col.key) === key
      );
      if (!col?.width) {
        return <th {...restProps}>{children}</th>;
      }

      const onDrag = (x) => {
        self.draggingMap[key] = 0;
        col.width = Math.max(x + 15, 50);
      };

      const onDragstop = () => {
        self.draggingMap[key] = thDom.getBoundingClientRect().width - 5;
      };

      return (
        <th
          {...restProps}
          v-ant-ref={(r) => (thDom = r)}
          width={col.width}
          class="resize-table-th"
        >
          {children}
          <vue-draggable-resizable
            key={col.key}
            class="table-draggable-handle"
            w={5}
            x={(self.draggingMap[key] || col.width) - 15}
            z={1}
            axis="x"
            draggable={true}
            resizable={false}
            onDragging={onDrag}
            onDragstop={onDragstop}
          ></vue-draggable-resizable>
        </th>
      );
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
      this.$emit("onSelectChange", selectedRowKeys);
    },
  },
};
</script>

<style lang="scss">
.resize-table-th {
  position: relative;

  .table-draggable-handle {
    position: absolute !important;
    height: 100% !important;
    bottom: 0;
    left: auto !important;
    cursor: col-resize;
    touch-action: none;
  }
}

.search-input {
  border-top: none;
  border-right: none;
  border-left: none;
  margin-bottom: 2px;
}

.ant-table-body table {
  table-layout: fixed;
  width: 100%;
}
</style>